/**
 * Second factors and the tenant's sign-in policy: the authentication
 * service's /api/v1/auth/mfa and /api/v1/auth/policy shapes, field for field.
 * docs/architecture/authentication.md, "Second factors", is the design.
 */
import type { MFAFactorKind, Session } from './types';

/** MFAFactor mirrors authservice domain.MFAFactor. */
export interface MFAFactor {
  id: string;
  kind: MFAFactorKind;
  /** The owner's name for it: "Phone", "YubiKey 5C". */
  label: string;
  confirmedAt?: string;
  createdAt: string;
  lastUsedAt?: string;
}

/** MFAStatus mirrors GET /api/v1/auth/mfa: the caller's own factors. */
export interface MFAStatus {
  factors: MFAFactor[];
  recoveryCodesLeft: number;
  /** The tenant's policy requires a factor of this user, so the last one
   * cannot be removed. */
  required: boolean;
  /** When an administrator last reset the user's factors. */
  resetAt?: string;
  hasPassword: boolean;
  /** The server can register security keys (it has a public origin). */
  securityKeysEnabled: boolean;
}

/** MFAMethod is a way to answer a sign-in challenge. */
export type MFAMethod = 'totp' | 'webauthn' | 'recovery';

/** MFAChallenge is a correct password that still needs a second factor: the
 * login endpoint's answer in place of a session. */
export interface MFAChallenge {
  mfaRequired: true;
  /** The sealed challenge to present with the factor; five minutes. */
  mfaToken: string;
  /** verify: use a factor. enroll: the policy requires one; set it up. */
  stage: 'verify' | 'enroll';
  methods: MFAMethod[];
  /** When an administrator reset the user's factors, not yet told. */
  mfaResetAt?: string;
}

/** LoginResult is what a password sign-in returns. */
export type LoginResult = Session | MFAChallenge;

/** isMFAChallenge tells a challenge from a session. */
export function isMFAChallenge(result: LoginResult): result is MFAChallenge {
  return 'mfaRequired' in result && result.mfaRequired;
}

/** TOTPEnrollment is an authenticator app waiting for its first code. The
 * secret is shown once. */
export interface TOTPEnrollment {
  factorId: string;
  /** Base32, for typing into an app. */
  secret: string;
  /** otpauth://totp/…, for a QR code or a tap on a phone. */
  uri: string;
}

/** ConfirmedFactor is a finished enrollment; a first factor comes with the
 * recovery codes, shown once. */
export interface ConfirmedFactor {
  factor: MFAFactor;
  recoveryCodes?: string[];
}

/** SessionWithRecoveryCodes is the session a sign-in enrollment opens. */
export interface SessionWithRecoveryCodes extends Session {
  recoveryCodes?: string[];
}

/** MFARequirement says whose password sign-ins need a second factor. */
export type MFARequirement = 'none' | 'admins' | 'everyone';

export const mfaRequirements: readonly MFARequirement[] = ['none', 'admins', 'everyone'];

/** AuthPolicy mirrors GET /api/v1/auth/policy. */
export interface AuthPolicy {
  mfaRequired: MFARequirement;
  updatedAt?: string;
}

/** SAMLConnection mirrors authservice domain.SAMLConnection. */
export interface SAMLConnection {
  idpEntityId: string;
  ssoUrl: string;
  certificatesPem: string;
  emailAttribute: string;
  nameAttribute: string;
  jitCreate: boolean;
  roleAttribute: string;
  adminValues: string[];
  enforce: boolean;
  allowPasswordAdmins: boolean;
  updatedAt?: string;
}

/** SAMLDomainStatus: pending until the domain's DNS carries the TXT record. */
export type SAMLDomainStatus = 'pending' | 'verified';

/** SAMLDomain mirrors authservice domain.SAMLDomain: a claim to an email
 * domain for the login page's SSO button, and the TXT record that proves
 * it. */
export interface SAMLDomain {
  domain: string;
  status: SAMLDomainStatus;
  recordName: string;
  recordValue: string;
  verifiedAt?: string;
  checkedAt?: string;
  createdAt: string;
}

/** SAMLSettings mirrors GET /api/v1/auth/saml. */
export interface SAMLSettings {
  /** False when the server has no public URL to build SAML endpoints from. */
  enabled: boolean;
  serviceProvider?: { entityId: string; acsUrl: string; metadataUrl: string };
  connection?: SAMLConnection;
  /** The tenant's email domain claims; absent when SAML is off. */
  domains?: SAMLDomain[];
}

/** SAMLInput is PUT /api/v1/auth/saml: metadataXml, or the three IdP fields. */
export interface SAMLInput {
  metadataXml?: string;
  idpEntityId?: string;
  ssoUrl?: string;
  certificatesPem?: string;
  emailAttribute: string;
  nameAttribute: string;
  jitCreate: boolean;
  roleAttribute: string;
  adminValues: string[];
  enforce: boolean;
  allowPasswordAdmins: boolean;
}

/** SAMLDiscovery is POST /api/v1/auth/saml/discover: where the SSO button
 * goes, and the organization the person is asked to confirm. */
export interface SAMLDiscovery {
  loginUrl: string;
  organization: string;
}
